import React from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../services/api';
import type { DocumentRecord } from '../types';

interface ContractAttachmentListProps {
  attachments: DocumentRecord[];
  // Opens the file on top of the current flow (review / signing) – never a new view.
  onOpen: (document: DocumentRecord) => void;
  // Opens the cryptographic details (SHA-256, signing rounds) for traceability.
  onVerify: (document: DocumentRecord) => void;
}

const fileExtension = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
};

const fileIcon = (name: string) => {
  const ext = fileExtension(name);
  if (['jpg', 'jpeg', 'png', 'heic'].includes(ext)) return 'feather-image';
  if (['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'].includes(ext)) return 'feather-file-text';
  return 'feather-file';
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The contract's attachments in the review and signing view: each file is
 * clearly listed, opens in a preview on top of the flow and can be downloaded.
 * The cryptographic verification stays available, but as a secondary action.
 */
export function ContractAttachmentList({ attachments, onOpen, onVerify }: ContractAttachmentListProps) {
  const { t } = useTranslation();

  if (attachments.length === 0) {
    return <p className="text-muted">{t('contracts.preview.noAttachments')}</p>;
  }

  return (
    <>
      <p className="text-muted small mb-2">{t('contracts.preview.attachmentsHelp')}</p>
      <ul className="list-unstyled mb-2 contract-attachment-list" data-testid="contract-attachment-list">
        {attachments.map((doc, index) => {
          const name = doc.originalName || doc.fileName;
          const ext = fileExtension(name).toUpperCase();
          return (
            <li
              key={doc.id}
              className="d-flex flex-wrap align-items-center gap-2 border rounded p-2 mb-2"
              data-testid="contract-attachment"
            >
              <button
                type="button"
                className="btn btn-link text-start text-decoration-none p-0 d-flex align-items-center gap-2 flex-grow-1 contract-attachment-open"
                onClick={() => onOpen(doc)}
                aria-label={t('contracts.attachments.openAria', { name })}
              >
                <i className={`${fileIcon(name)} contract-attachment-file-icon fs-4 text-primary flex-shrink-0`} aria-hidden="true"></i>
                <span className="d-flex flex-column contract-attachment-text">
                  <span className="fw-semibold text-break">
                    {t('contracts.preview.attachmentLine', { number: index + 1, name })}
                  </span>
                  <small className="text-muted">
                    {[ext, formatBytes(doc.size), new Date(doc.createdAt).toLocaleDateString('sv-SE')]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
              </button>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-muted contract-attachment-action"
                  onClick={() => onVerify(doc)}
                  aria-label={t('contracts.attachments.verifyAria', { name })}
                >
                  <i className="feather-shield me-1" aria-hidden="true"></i>
                  {t('contracts.attachments.verify')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm contract-attachment-action"
                  onClick={() => void documentApi.downloadFile(doc.id, name)}
                  aria-label={t('contracts.attachments.downloadAria', { name })}
                >
                  <i className="feather-download me-1" aria-hidden="true"></i>
                  {t('contracts.attachments.download')}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
